import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Loader2, Check, Trash2, Pencil, Search, MailWarning, Inbox } from 'lucide-react';
import { ContactHoverCard } from './ContactHoverCard';
import { CollapsibleSection } from './CollapsibleSection';

export const CONTACT_ROLE_LABELS = {
  owner: 'Owner', shareholder: 'Shareholder', broker: 'Broker',
  lawyer: 'Lawyer', tax_advisor: 'Tax Advisor', other: 'Other',
};
const ROLE_COLORS = {
  owner: 'text-emerald-400 border-emerald-800/40',
  shareholder: 'text-emerald-400 border-emerald-800/40',
  broker: 'text-blue-400 border-blue-800/30',
  lawyer: 'text-amber-400 border-amber-800/30',
  tax_advisor: 'text-amber-400 border-amber-800/30',
  other: 'text-muted-foreground border-border',
};

function ContactForm({ initial, onSave, saving }) {
  const [name, setName] = useState(initial?.name || '');
  const [role, setRole] = useState(initial?.role || 'owner');
  const [roleOther, setRoleOther] = useState(initial?.role_other || '');
  const [emails, setEmails] = useState((initial?.emails || ['']).join(', '));

  const submit = () => {
    const list = emails.split(/[,;\s]+/).map(e => e.trim()).filter(e => e.includes('@'));
    if (!name.trim()) { toast.error('Please enter a name'); return; }
    if (!list.length) { toast.error('Please enter at least one valid email address'); return; }
    onSave({ name: name.trim(), role, role_other: roleOther, emails: list });
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Name</p>
        <Input value={name} onChange={e => setName(e.target.value)} data-testid="contact-name-input"
          className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" />
      </div>
      <div>
        <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Role</p>
        <div className="flex gap-1.5 flex-wrap">
          {Object.entries(CONTACT_ROLE_LABELS).map(([value, label]) => (
            <button key={value} onClick={() => setRole(value)} data-testid={`contact-role-${value}`}
              className={`text-xs border rounded-sm px-2.5 h-7 transition-colors ${role === value ? 'border-emerald-500 bg-emerald-500/15 text-emerald-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
              {label}
            </button>
          ))}
        </div>
        {role === 'other' && (
          <Input value={roleOther} onChange={e => setRoleOther(e.target.value)} placeholder="Role description..."
            data-testid="contact-role-other-input"
            className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs mt-1.5 w-56" />
        )}
      </div>
      <div>
        <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Email address(es) — comma-separated</p>
        <Input value={emails} onChange={e => setEmails(e.target.value)} data-testid="contact-emails-input"
          placeholder="owner@company.de, private@gmail.com"
          className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" />
      </div>
      <div className="flex justify-end">
        <Button onClick={submit} disabled={saving} data-testid="contact-save-btn"
          className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-8 px-4">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
          Save contact
        </Button>
      </div>
    </div>
  );
}

function CorrespondencePicker({ onPick }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noPermission, setNoPermission] = useState(false);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const r = await api.getRecentCorrespondents(query);
      setItems(r.data.correspondents || []);
      setNoPermission(false);
    } catch (err) {
      if (err.response?.status === 409) setNoPermission(true);
      else toast.error(err.response?.data?.detail || 'Failed to load correspondence');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(''); }, [load]);

  if (noPermission) return <MailPermissionHint />;
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={e => { setQ(e.target.value); load(e.target.value); }}
          placeholder="Search by name or email..." data-testid="correspondence-search-input"
          className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm pl-8" />
      </div>
      <div className="max-h-64 overflow-y-auto space-y-0.5" data-testid="correspondence-list">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground italic py-4 text-center">No correspondence found.</p>
        ) : items.map(c => (
          <button key={c.email} onClick={() => onPick(c)} data-testid={`correspondent-${c.email}`}
            className="w-full text-left px-2.5 py-2 rounded-sm hover:bg-secondary/50 transition-colors">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground truncate">{c.name || c.email}</span>
              <span className="text-[10px] text-muted-foreground font-mono ml-auto shrink-0">
                {c.count} mail{c.count !== 1 ? 's' : ''} · {(c.last_at || '').slice(0, 10)}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground truncate">{c.email}{c.last_subject ? ` — ${c.last_subject}` : ''}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export function MailPermissionHint() {
  const [starting, setStarting] = useState(false);
  const connect = async () => {
    setStarting(true);
    try {
      const r = await api.mstodoConnect();
      window.location.href = r.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start Microsoft consent');
      setStarting(false);
    }
  };
  return (
    <div className="border border-amber-600/40 bg-amber-500/10 rounded-sm p-3 flex items-start gap-2.5" data-testid="mail-permission-hint">
      <MailWarning className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-xs text-amber-300 font-medium">E-Mail-Zugriff erlauben</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Your Microsoft connection does not include mailbox read access yet. Re-connect once to grant it —
          everything else keeps working if you skip this.
        </p>
        <Button size="sm" onClick={connect} disabled={starting} data-testid="mail-reconsent-btn"
          className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm text-[11px] h-7 px-3 mt-2">
          {starting ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : null}
          Allow email access
        </Button>
      </div>
    </div>
  );
}

export function DealContactsCard({ projectId, companyName = '' }) {
  const [contacts, setContacts] = useState([]);
  const [permission, setPermission] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState('manual'); // manual | pick
  const [editContact, setEditContact] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.getDealContacts(projectId);
      setContacts(r.data.contacts || []);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => {
    load();
    api.getMailPermission().then(r => setPermission(r.data)).catch(() => {});
  }, [load]);

  const openAdd = () => { setEditContact(null); setPrefill(null); setMode('manual'); setDialogOpen(true); };

  const handleSave = async (data) => {
    setSaving(true);
    try {
      if (editContact) {
        await api.updateDealContact(editContact.id, data);
        toast.success('Contact updated');
      } else {
        await api.addDealContact(projectId, data);
        toast.success('Contact added — emails will sync on next refresh');
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save contact'); }
    setSaving(false);
  };

  const handleDelete = async (c) => {
    try {
      await api.deleteDealContact(c.id);
      toast.success(`${c.name} removed (incl. synced emails)`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <CollapsibleSection
      storageKey={`deal.${projectId}.contacts`}
      icon={Users}
      iconClass="text-emerald-400"
      title="Contacts"
      count={contacts.length}
      testId="deal-contacts-card"
      bodyClass="p-4"
      right={
        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openAdd(); }} data-testid="add-contact-btn"
          className="border-emerald-600/40 text-emerald-400 hover:bg-emerald-600/10 rounded-sm text-[11px] h-7 px-2.5">
          <Plus className="w-3 h-3 mr-1" /> Add contact
        </Button>
      }
    >

      {permission && permission.connected && !permission.mail_read_ready && <div className="mb-3"><MailPermissionHint /></div>}

      {contacts.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No contacts yet — add the owner (manually or from your email correspondence) to start email tracking for this deal.
        </p>
      ) : (
        <div className="space-y-1.5">
          {contacts.map(c => (
            <div key={c.id} className="flex items-center gap-2.5 px-2.5 py-2 bg-secondary/30 border border-border rounded-sm group"
              data-testid={`contact-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <ContactHoverCard name={c.name} email={(c.emails || [])[0] || ''} company={companyName} projectId={projectId} canCreate>
                    <span className="text-xs font-medium text-foreground truncate cursor-pointer underline decoration-dotted decoration-foreground/20 underline-offset-2 hover:decoration-foreground/60">{c.name}</span>
                  </ContactHoverCard>
                  <span className={`text-[9px] font-mono uppercase border rounded px-1.5 py-0.5 bg-secondary shrink-0 ${ROLE_COLORS[c.role] || ROLE_COLORS.other}`}>
                    {c.role === 'other' && c.role_other ? c.role_other : CONTACT_ROLE_LABELS[c.role] || c.role}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground truncate font-mono">{(c.emails || []).join(' · ')}</p>
              </div>
              <button onClick={() => { setEditContact(c); setPrefill(null); setMode('manual'); setDialogOpen(true); }}
                data-testid={`edit-contact-${c.id}`}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-all">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleDelete(c)} data-testid={`delete-contact-${c.id}`}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-400 transition-all">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground">
              {editContact ? 'Edit contact' : 'Add contact'}
            </DialogTitle>
          </DialogHeader>
          {!editContact && (
            <div className="flex gap-1.5">
              <button onClick={() => setMode('manual')} data-testid="contact-mode-manual"
                className={`flex items-center gap-1.5 text-xs border rounded-sm px-3 h-8 transition-colors ${mode === 'manual' ? 'border-emerald-500 bg-emerald-500/15 text-emerald-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                <Pencil className="w-3 h-3" /> Manual
              </button>
              <button onClick={() => setMode('pick')} data-testid="contact-mode-pick"
                className={`flex items-center gap-1.5 text-xs border rounded-sm px-3 h-8 transition-colors ${mode === 'pick' ? 'border-emerald-500 bg-emerald-500/15 text-emerald-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                <Inbox className="w-3 h-3" /> From correspondence
              </button>
            </div>
          )}
          {mode === 'pick' && !editContact && !prefill ? (
            <CorrespondencePicker onPick={(c) => { setPrefill({ name: c.name, emails: [c.email] }); setMode('manual'); }} />
          ) : (
            <ContactForm key={(editContact?.id || '') + (prefill?.emails?.[0] || '')}
              initial={editContact || prefill} onSave={handleSave} saving={saving} />
          )}
        </DialogContent>
      </Dialog>
    </CollapsibleSection>
  );
}
